import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./RunPayrollPage.css";

const AdminRunPayroll = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [hourlyEmployees, setHourlyEmployees] = useState([]);
  const [salariedEmployees, setSalariedEmployees] = useState([]);
  const [payPeriod, setPayPeriod] = useState(null);

  // Editable pay period fields
  const [payPeriodForm, setPayPeriodForm] = useState({
    start_date: "",
    end_date: "",
    pay_date: "",
    frequency: "biweekly",
  });

  // Per-employee inputs (now includes editable rates)
  const [employeeInputs, setEmployeeInputs] = useState({});

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const frequencyMultiplier = useMemo(
    () => ({
      weekly: 52,
      biweekly: 26,
      semimonthly: 24,
      monthly: 12,
    }),
    []
  );

  // helpers for <input type="date" />
  const toYYYYMMDD = (dateLike) => {
    if (!dateLike) return "";
    const d = new Date(dateLike);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getPayPeriodSalaryFromAnnual = (annualSalary) => {
    const freq = (payPeriodForm.frequency || payPeriod?.frequency || "").toLowerCase();
    const multiplier = frequencyMultiplier[freq] || 1;
    return annualSalary && multiplier ? (annualSalary / multiplier).toFixed(2) : "";
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const payPeriodResponse = await api.get(`/business/${businessId}/pay-period`);
        const pp = payPeriodResponse.data;
        setPayPeriod(pp);

        // Seed form with backend pay period values
        setPayPeriodForm({
          start_date: toYYYYMMDD(pp?.start_date),
          end_date: toYYYYMMDD(pp?.end_date),
          pay_date: toYYYYMMDD(pp?.pay_date),
          frequency: (pp?.frequency || "biweekly").toLowerCase(),
        });

        const hourlyResponse = await api.get(`/businesses/${businessId}/employees/hourly`);
        const hourly = hourlyResponse.data.map((employee) => ({
          ...employee,
          type: "hourly",
        }));
        setHourlyEmployees(hourly);

        const salariedResponse = await api.get(`/businesses/${businessId}/employees/salary`);
        const salaried = salariedResponse.data.map((employee) => ({
          ...employee,
          type: "salaried",
        }));
        setSalariedEmployees(salaried);

        // Initialize employeeInputs with editable rates
        const initialInputs = {};
        [...hourly, ...salaried].forEach((employee) => {
          initialInputs[employee.id] = {
            skip: "no",

            // NEW: editable rates (user will enter these)
            hourly_rate: employee.type === "hourly" ? null : null,
            annual_salary: employee.type === "salaried" ? null : null,

            regularHours: null,
            overtimeHours: null,
            doubletimeHours: null,
            cashAdvance: null,
            bonus: null,
            tips: null,
            reimbursement: null,
            other: null,
            vacationHours: null,
            vacationPay: null,
          };
        });
        setEmployeeInputs(initialInputs);

        setError(null);
      } catch (err) {
        console.error("Error fetching employees:", err);
        setError("Failed to fetch employees. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId]);

  const handlePayPeriodChange = (field, value) => {
    setPayPeriodForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInputChange = (employeeId, field, value) => {
    setEmployeeInputs((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        [field]: value === "" ? null : parseFloat(value),
      },
    }));
  };

  const handleSkipChange = (employeeId, value) => {
    setEmployeeInputs((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        skip: value, // "yes" or "no"
      },
    }));
  };

  const handleSubmit = async () => {
    const startDate = payPeriodForm.start_date || toYYYYMMDD(payPeriod?.start_date);
    const endDate = payPeriodForm.end_date || toYYYYMMDD(payPeriod?.end_date);
    const payDate = payPeriodForm.pay_date || toYYYYMMDD(payPeriod?.pay_date);
    const frequency = (payPeriodForm.frequency || payPeriod?.frequency || "").toLowerCase();

    // Build payload, excluding skipped employees
    const collectedResults = [...hourlyEmployees, ...salariedEmployees]
      .filter((employee) => (employeeInputs[employee.id]?.skip || "no") !== "yes")
      .map((employee) => {
        const employeeInput = employeeInputs[employee.id] || {};

        const extraFields = {
          cashAdvance: employeeInput.cashAdvance || 0,
          bonus: employeeInput.bonus || 0,
          tips: employeeInput.tips || 0,
          reimbursement: employeeInput.reimbursement || 0,
          other: employeeInput.other || 0,
        };

        // per-hour salary for salaried employees (based on entered annual salary)
        const annualSalary = employeeInput.annual_salary || 0;
        const perhourSalary = annualSalary ? annualSalary / 52 / 40 : null;

        const commonFields = {
          id: employee.id,
          business_id: businessId,
          business_state: payPeriod?.b_state,
          start_date: startDate,
          end_date: endDate,
          pay_date: payDate,
          frequency,
          name: `${employee.first_name} ${employee.last_name}`,
          st_add: employee.e_st_add,
          e_suite: employee.e_suite,
          e_zip: employee.e_zip,
          filing_status: employee.filing_status,
          e_state: employee.e_state,
          e_city: employee.e_city,
          e_county: employee.e_county,
          dependents: employee.dependents || 0,
          contrib_401k: employee.contrib_401k || 0,
          donation: employee.donation || 0,
          garnishment: employee.garnishment || 0,
          additional_fed_tax: employee.additional_fed_tax || 0,
          additional_state_tax: employee.additional_state_tax || 0,
          work_loc: employee.work_loc,
          perhour_salary: perhourSalary,
          payment_type: employee.payment_type,
          employer_mandated: employee.employer_mandated,
          is_direct_dep: employee.is_direct_dep,
          account_number: employee.account_number,
          routing_number: employee.routing_number,
        };

        if (employee.type === "hourly") {
          return {
            ...commonFields,
            ...extraFields,

            // NEW: use entered hourly rate instead of employee.hourly_amt
            hourly_rate: employeeInput.hourly_rate || 0,

            regularHours:
              employeeInput.regularHours !== null && employeeInput.regularHours !== undefined
                ? employeeInput.regularHours
                : 0,
            overtimeHours: employeeInput.overtimeHours || 0,
            doubletimeHours: employeeInput.doubletimeHours || 0,
            vacationHours: employeeInput.vacationHours || 0,
          };
        }

        // NEW: use entered annual salary to compute per-period salary_amt
        const perPeriodSalary = Number(getPayPeriodSalaryFromAnnual(employeeInput.annual_salary));

        return {
          ...commonFields,
          ...extraFields,
          salary_amt: Number.isFinite(perPeriodSalary) ? perPeriodSalary : 0,
          vacationPay: employeeInput.vacationPay || 0,
        };
      });

    // Basic frontend validation for rates (optional but recommended)
    const missingRates = [];
    [...hourlyEmployees, ...salariedEmployees].forEach((emp) => {
      const inp = employeeInputs[emp.id];
      if (!inp) return;
      if (inp.skip === "yes") return;

      if (emp.type === "hourly") {
        if (inp.hourly_rate === null || Number.isNaN(inp.hourly_rate)) {
          missingRates.push(`${emp.first_name} ${emp.last_name} (Hourly Rate)`);
        }
      } else {
        if (inp.annual_salary === null || Number.isNaN(inp.annual_salary)) {
          missingRates.push(`${emp.first_name} ${emp.last_name} (Annual Salary)`);
        }
      }
    });

    if (missingRates.length > 0) {
      alert(`Please enter rates for:\n\n${missingRates.map((m) => `- ${m}`).join("\n")}`);
      return;
    }

    try {
      const response = await api.post(`/payroll/admin-entry`, { payrollData: collectedResults });
      const { success, errors } = response.data;

      if (errors?.length > 0) {
        alert(
          `Partial success:\n\nSuccess:\n${success
            .map((item) => ` - ${item.name}`)
            .join("\n")}\n\nErrors:\n${errors.join("\n")}`
        );
      } else {
        alert("All payroll records processed successfully.");
        navigate(`/dashboard/business/${businessId}/viewpayroll`);
      }
    } catch (err) {
      console.error("Error submitting payroll data:", err);
      alert("Failed to submit payroll data. Please try again.");
    }
  };

  if (loading) return <p>Loading employees...</p>;
  if (error) return <p>{error}</p>;

  return (
    <div className="run-body">
      <h2>New Payroll</h2>

      {/* editable pay period section */}
      <div className="pay-period-payroll-cont">
        <div className="ppl-pay">
          <label>Start Date:</label>
          <input
            type="date"
            className="pay-period-content"
            value={payPeriodForm.start_date}
            onChange={(e) => handlePayPeriodChange("start_date", e.target.value)}
          />
        </div>

        <div className="ppl-pay">
          <label>End Date:</label>
          <input
            type="date"
            className="pay-period-content"
            value={payPeriodForm.end_date}
            onChange={(e) => handlePayPeriodChange("end_date", e.target.value)}
          />
        </div>

        <div className="ppl-pay">
          <label>Pay Date:</label>
          <input
            type="date"
            className="pay-period-content"
            value={payPeriodForm.pay_date}
            onChange={(e) => handlePayPeriodChange("pay_date", e.target.value)}
          />
        </div>

        <div className="ppl-pay">
          <label>Frequency:</label>
          <select
            className="pay-period-content"
            value={payPeriodForm.frequency}
            onChange={(e) => handlePayPeriodChange("frequency", e.target.value)}
          >
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly</option>
            <option value="semimonthly">Semimonthly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      <table className="run-table">
        <tbody className="run-tbody">
          {[...hourlyEmployees, ...salariedEmployees].map((employee) => {
            const isSkipped = (employeeInputs[employee.id]?.skip || "no") === "yes";

            const hourlyRateVal = employeeInputs[employee.id]?.hourly_rate ?? "";
            const annualSalaryVal = employeeInputs[employee.id]?.annual_salary ?? "";

            const displaySalaryPerPeriod =
              employee.type === "salaried" && employeeInputs[employee.id]?.annual_salary
                ? `$${getPayPeriodSalaryFromAnnual(employeeInputs[employee.id]?.annual_salary)} per period`
                : "";

            return (
              <tr className="run-row" key={employee.id}>
                <td>
                  {employee.first_name} {employee.last_name}
                </td>

                <td>{employee.type === "hourly" ? "Hourly" : "Salaried"}</td>

                {/* NEW: enter rate here */}
                <td>
                  {employee.type === "hourly" ? (
                    <>
                      <input
                        type="number"
                        placeholder="Hourly Rate"
                        disabled={isSkipped}
                        value={hourlyRateVal}
                        onChange={(e) =>
                          handleInputChange(employee.id, "hourly_rate", e.target.value)
                        }
                      />
                      {employeeInputs[employee.id]?.hourly_rate !== null &&
                        employeeInputs[employee.id]?.hourly_rate !== undefined &&
                        employeeInputs[employee.id]?.hourly_rate !== "" && (
                          <div style={{ marginTop: "4px" }}>
                            ${Number(employeeInputs[employee.id]?.hourly_rate || 0).toFixed(2)} / hr
                          </div>
                        )}
                    </>
                  ) : (
                    <>
                      <input
                        type="number"
                        placeholder="Annual Salary"
                        disabled={isSkipped}
                        value={annualSalaryVal}
                        onChange={(e) =>
                          handleInputChange(employee.id, "annual_salary", e.target.value)
                        }
                      />
                      {displaySalaryPerPeriod && <div style={{ marginTop: "4px" }}>{displaySalaryPerPeriod}</div>}
                    </>
                  )}
                </td>

                <td>
                  {/* Skip select */}
                  <select
                    value={employeeInputs[employee.id]?.skip || "no"}
                    onChange={(e) => handleSkipChange(employee.id, e.target.value)}
                    style={{ marginBottom: "8px" }}
                  >
                    <option value="no">Do Not Skip</option>
                    <option value="yes">Skip</option>
                  </select>

                  {employee.type === "hourly" && (
                    <>
                      <input
                        type="number"
                        placeholder="Regular Hours"
                        disabled={isSkipped}
                        value={employeeInputs[employee.id]?.regularHours ?? ""}
                        onChange={(e) =>
                          handleInputChange(employee.id, "regularHours", e.target.value)
                        }
                      />
                      <input
                        type="number"
                        placeholder="Overtime Hours"
                        disabled={isSkipped}
                        value={employeeInputs[employee.id]?.overtimeHours ?? ""}
                        onChange={(e) =>
                          handleInputChange(employee.id, "overtimeHours", e.target.value)
                        }
                      />
                      <input
                        type="number"
                        placeholder="Doubletime Hours"
                        disabled={isSkipped}
                        value={employeeInputs[employee.id]?.doubletimeHours ?? ""}
                        onChange={(e) =>
                          handleInputChange(employee.id, "doubletimeHours", e.target.value)
                        }
                      />
                      <input
                        type="number"
                        placeholder="Vacation Hours"
                        disabled={isSkipped}
                        value={employeeInputs[employee.id]?.vacationHours ?? ""}
                        onChange={(e) =>
                          handleInputChange(employee.id, "vacationHours", e.target.value)
                        }
                      />
                    </>
                  )}

                  {employee.type === "salaried" && (
                    <input
                      type="number"
                      placeholder="Vacation Pay"
                      disabled={isSkipped}
                      value={employeeInputs[employee.id]?.vacationPay ?? ""}
                      onChange={(e) =>
                        handleInputChange(employee.id, "vacationPay", e.target.value)
                      }
                    />
                  )}

                  <input
                    type="number"
                    placeholder="Cash Advance"
                    disabled={isSkipped}
                    value={employeeInputs[employee.id]?.cashAdvance ?? ""}
                    onChange={(e) =>
                      handleInputChange(employee.id, "cashAdvance", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    placeholder="Bonus"
                    disabled={isSkipped}
                    value={employeeInputs[employee.id]?.bonus ?? ""}
                    onChange={(e) => handleInputChange(employee.id, "bonus", e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder="Tips"
                    disabled={isSkipped}
                    value={employeeInputs[employee.id]?.tips ?? ""}
                    onChange={(e) => handleInputChange(employee.id, "tips", e.target.value)}
                  />
                  <input
                    type="number"
                    placeholder="Reimbursement"
                    disabled={isSkipped}
                    value={employeeInputs[employee.id]?.reimbursement ?? ""}
                    onChange={(e) =>
                      handleInputChange(employee.id, "reimbursement", e.target.value)
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="button-container">
        <button onClick={handleSubmit}>Submit</button>
      </div>
    </div>
  );
};

export default AdminRunPayroll;



// import React, { useEffect, useMemo, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./RunPayrollPage.css";

// const AdminRunPayroll = () => {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();

//   const [hourlyEmployees, setHourlyEmployees] = useState([]);
//   const [salariedEmployees, setSalariedEmployees] = useState([]);
//   const [payPeriod, setPayPeriod] = useState(null);

//   // Editable fields for pay period
//   const [payPeriodForm, setPayPeriodForm] = useState({
//     start_date: "",
//     end_date: "",
//     pay_date: "",
//     frequency: "biweekly",
//   });

//   const [employeeInputs, setEmployeeInputs] = useState({});
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const frequencyMultiplier = useMemo(
//     () => ({
//       weekly: 52,
//       biweekly: 26,
//       semimonthly: 24,
//       monthly: 12,
//     }),
//     []
//   );

//   // helpers for <input type="date" />
//   const toYYYYMMDD = (dateLike) => {
//     if (!dateLike) return "";
//     const d = new Date(dateLike);
//     if (Number.isNaN(d.getTime())) return "";
//     const yyyy = d.getFullYear();
//     const mm = String(d.getMonth() + 1).padStart(2, "0");
//     const dd = String(d.getDate()).padStart(2, "0");
//     return `${yyyy}-${mm}-${dd}`;
//   };

//   const getPayPeriodSalary = (annualSalary) => {
//     const freq = (payPeriodForm.frequency || payPeriod?.frequency || "").toLowerCase();
//     const multiplier = frequencyMultiplier[freq] || 1;
//     return (annualSalary / multiplier).toFixed(2);
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const payPeriodResponse = await api.get(`/business/${businessId}/pay-period`);
//         const pp = payPeriodResponse.data;
//         setPayPeriod(pp);

//         // Seed form with backend pay period values
//         setPayPeriodForm({
//           start_date: toYYYYMMDD(pp?.start_date),
//           end_date: toYYYYMMDD(pp?.end_date),
//           pay_date: toYYYYMMDD(pp?.pay_date),
//           frequency: (pp?.frequency || "biweekly").toLowerCase(),
//         });

//         const hourlyResponse = await api.get(`/businesses/${businessId}/employees/hourly`);
//         const hourly = hourlyResponse.data.map((employee) => ({
//           ...employee,
//           type: "hourly",
//         }));
//         setHourlyEmployees(hourly);

//         const salariedResponse = await api.get(`/businesses/${businessId}/employees/salary`);
//         const salaried = salariedResponse.data.map((employee) => ({
//           ...employee,
//           type: "salaried",
//         }));
//         setSalariedEmployees(salaried);

//         const initialInputs = {};
//         [...hourly, ...salaried].forEach((employee) => {
//           initialInputs[employee.id] = {
//             skip: "no", // NEW
//             regularHours: null,
//             overtimeHours: null,
//             doubletimeHours: null,
//             cashAdvance: null,
//             bonus: null,
//             tips: null,
//             reimbursement: null,
//             other: null,
//             vacationHours: null,
//             vacationPay: null,
//           };
//         });
//         setEmployeeInputs(initialInputs);

//         setError(null);
//       } catch (err) {
//         console.error("Error fetching employees:", err);
//         setError("Failed to fetch employees. Please try again.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [businessId]);

//   const handlePayPeriodChange = (field, value) => {
//     setPayPeriodForm((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const handleInputChange = (employeeId, field, value) => {
//     setEmployeeInputs((prev) => ({
//       ...prev,
//       [employeeId]: {
//         ...prev[employeeId],
//         [field]: value ? parseFloat(value) : null,
//       },
//     }));
//   };

//   // NEW: skip select handler (string, not float)
//   const handleSkipChange = (employeeId, value) => {
//     setEmployeeInputs((prev) => ({
//       ...prev,
//       [employeeId]: {
//         ...prev[employeeId],
//         skip: value, // "yes" or "no"
//       },
//     }));
//   };

//   const handleSubmit = async () => {
//     const startDate = payPeriodForm.start_date || toYYYYMMDD(payPeriod?.start_date);
//     const endDate = payPeriodForm.end_date || toYYYYMMDD(payPeriod?.end_date);
//     const payDate = payPeriodForm.pay_date || toYYYYMMDD(payPeriod?.pay_date);
//     const frequency = (payPeriodForm.frequency || payPeriod?.frequency || "").toLowerCase();

//     const collectedResults = [...hourlyEmployees, ...salariedEmployees]
//       // NEW: exclude skipped employees
//       .filter((employee) => (employeeInputs[employee.id]?.skip || "no") !== "yes")
//       .map((employee) => {
//         const perhourSalary = employee.salary_amt / 52 / 40 || null;
//         const employeeInput = employeeInputs[employee.id] || {};

//         const extraFields = {
//           cashAdvance: employeeInput.cashAdvance || 0,
//           bonus: employeeInput.bonus || 0,
//           tips: employeeInput.tips || 0,
//           reimbursement: employeeInput.reimbursement || 0,
//           other: employeeInput.other || 0,
//         };

//         const commonFields = {
//           id: employee.id,
//           business_id: businessId,
//           business_state: payPeriod?.b_state,
//           start_date: startDate,
//           end_date: endDate,
//           pay_date: payDate,
//           frequency,
//           name: `${employee.first_name} ${employee.last_name}`,
//           st_add: employee.e_st_add,
//           e_suite: employee.e_suite,
//           e_zip: employee.e_zip,
//           filing_status: employee.filing_status,
//           e_state: employee.e_state,
//           e_city: employee.e_city,
//           e_county: employee.e_county,
//           dependents: employee.dependents || 0,
//           contrib_401k: employee.contrib_401k || 0,
//           donation: employee.donation || 0,
//           garnishment: employee.garnishment || 0,
//           additional_fed_tax: employee.additional_fed_tax || 0,
//           additional_state_tax: employee.additional_state_tax || 0,
//           work_loc: employee.work_loc,
//           perhour_salary: perhourSalary,
//           payment_type: employee.payment_type,
//           employer_mandated: employee.employer_mandated,
//           is_direct_dep: employee.is_direct_dep,
//           account_number: employee.account_number,
//           routing_number: employee.routing_number,
//         };

//         if (employee.type === "hourly") {
//           return {
//             ...commonFields,
//             ...extraFields,
//             hourly_rate: employee.hourly_amt,
//             regularHours:
//               employeeInput.regularHours !== null && employeeInput.regularHours !== undefined
//                 ? employeeInput.regularHours
//                 : 0,
//             overtimeHours: employeeInput.overtimeHours || 0,
//             doubletimeHours: employeeInput.doubletimeHours || 0,
//             vacationHours: employeeInput.vacationHours || 0,
//           };
//         }

//         return {
//           ...commonFields,
//           ...extraFields,
//           salary_amt: Number(getPayPeriodSalary(employee.salary_amt)),
//           vacationPay: employeeInput.vacationPay || 0,
//         };
//       });

//     try {
//       const response = await api.post(`/payroll/admin-entry`, { payrollData: collectedResults });
//       const { success, errors } = response.data;

//       if (errors?.length > 0) {
//         alert(
//           `Partial success:\n\nSuccess:\n${success
//             .map((item) => ` - ${item.name}`)
//             .join("\n")}\n\nErrors:\n${errors.join("\n")}`
//         );
//       } else {
//         alert("All payroll records processed successfully.");
//         navigate(`/dashboard/business/${businessId}/viewpayroll`);
//       }
//     } catch (err) {
//       console.error("Error submitting payroll data:", err);
//       alert("Failed to submit payroll data. Please try again.");
//     }
//   };

//   if (loading) return <p>Loading employees...</p>;
//   if (error) return <p>{error}</p>;

//   return (
//     <div className="run-body">
//       <h2>New Payroll</h2>

//       {/* editable pay period section (keeps same container class so your CSS still applies) */}
//       <div className="pay-period-payroll-cont">
//         <div className="ppl-pay">
//           <label>Start Date:</label>
//           <input
//             type="date"
//             className="pay-period-content"
//             value={payPeriodForm.start_date}
//             onChange={(e) => handlePayPeriodChange("start_date", e.target.value)}
//           />
//         </div>

//         <div className="ppl-pay">
//           <label>End Date:</label>
//           <input
//             type="date"
//             className="pay-period-content"
//             value={payPeriodForm.end_date}
//             onChange={(e) => handlePayPeriodChange("end_date", e.target.value)}
//           />
//         </div>

//         <div className="ppl-pay">
//           <label>Pay Date:</label>
//           <input
//             type="date"
//             className="pay-period-content"
//             value={payPeriodForm.pay_date}
//             onChange={(e) => handlePayPeriodChange("pay_date", e.target.value)}
//           />
//         </div>

//         <div className="ppl-pay">
//           <label>Frequency:</label>
//           <select
//             className="pay-period-content"
//             value={payPeriodForm.frequency}
//             onChange={(e) => handlePayPeriodChange("frequency", e.target.value)}
//           >
//             <option value="weekly">Weekly</option>
//             <option value="biweekly">Biweekly</option>
//             <option value="semimonthly">Semimonthly</option>
//             <option value="monthly">Monthly</option>
//           </select>
//         </div>
//       </div>

//       <table className="run-table">
//         <tbody className="run-tbody">
//           {[...hourlyEmployees, ...salariedEmployees].map((employee) => {
//             const isSkipped = (employeeInputs[employee.id]?.skip || "no") === "yes";

//             return (
//               <tr className="run-row" key={employee.id}>
//                 <td>
//                   {employee.first_name} {employee.last_name}
//                 </td>
//                 <td>{employee.type === "hourly" ? "Hourly" : "Salaried"}</td>
//                 <td>
//                   {employee.type === "hourly"
//                     ? `$${employee.hourly_amt} per hour`
//                     : `$${getPayPeriodSalary(employee.salary_amt)} per period`}
//                 </td>

//                 <td>
//                   {/* NEW: Skip select */}
//                   <select
//                     value={employeeInputs[employee.id]?.skip || "no"}
//                     onChange={(e) => handleSkipChange(employee.id, e.target.value)}
//                     style={{ marginBottom: "8px" }}
//                   >
//                     <option value="no">Do Not Skip</option>
//                     <option value="yes">Skip</option>
//                   </select>

//                   {employee.type === "hourly" && (
//                     <>
//                       <input
//                         type="number"
//                         placeholder="Regular Hours"
//                         disabled={isSkipped}
//                         value={employeeInputs[employee.id]?.regularHours ?? ""}
//                         onChange={(e) =>
//                           handleInputChange(employee.id, "regularHours", e.target.value)
//                         }
//                       />
//                       <input
//                         type="number"
//                         placeholder="Overtime Hours"
//                         disabled={isSkipped}
//                         value={employeeInputs[employee.id]?.overtimeHours ?? ""}
//                         onChange={(e) =>
//                           handleInputChange(employee.id, "overtimeHours", e.target.value)
//                         }
//                       />
//                       <input
//                         type="number"
//                         placeholder="Doubletime Hours"
//                         disabled={isSkipped}
//                         value={employeeInputs[employee.id]?.doubletimeHours ?? ""}
//                         onChange={(e) =>
//                           handleInputChange(employee.id, "doubletimeHours", e.target.value)
//                         }
//                       />
//                       <input
//                         type="number"
//                         placeholder="Vacation Hours"
//                         disabled={isSkipped}
//                         value={employeeInputs[employee.id]?.vacationHours ?? ""}
//                         onChange={(e) =>
//                           handleInputChange(employee.id, "vacationHours", e.target.value)
//                         }
//                       />
//                     </>
//                   )}

//                   {employee.type === "salaried" && (
//                     <input
//                       type="number"
//                       placeholder="Vacation Pay"
//                       disabled={isSkipped}
//                       value={employeeInputs[employee.id]?.vacationPay ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(employee.id, "vacationPay", e.target.value)
//                       }
//                     />
//                   )}

//                   <input
//                     type="number"
//                     placeholder="Cash Advance"
//                     disabled={isSkipped}
//                     value={employeeInputs[employee.id]?.cashAdvance ?? ""}
//                     onChange={(e) =>
//                       handleInputChange(employee.id, "cashAdvance", e.target.value)
//                     }
//                   />
//                   <input
//                     type="number"
//                     placeholder="Bonus"
//                     disabled={isSkipped}
//                     value={employeeInputs[employee.id]?.bonus ?? ""}
//                     onChange={(e) => handleInputChange(employee.id, "bonus", e.target.value)}
//                   />
//                   <input
//                     type="number"
//                     placeholder="Tips"
//                     disabled={isSkipped}
//                     value={employeeInputs[employee.id]?.tips ?? ""}
//                     onChange={(e) => handleInputChange(employee.id, "tips", e.target.value)}
//                   />
//                   <input
//                     type="number"
//                     placeholder="Reimbursement"
//                     disabled={isSkipped}
//                     value={employeeInputs[employee.id]?.reimbursement ?? ""}
//                     onChange={(e) =>
//                       handleInputChange(employee.id, "reimbursement", e.target.value)
//                     }
//                   />
//                 </td>
//               </tr>
//             );
//           })}
//         </tbody>
//       </table>

//       <div className="button-container">
//         <button onClick={handleSubmit}>Submit</button>
//       </div>
//     </div>
//   );
// };

// export default AdminRunPayroll;




// import React, { useEffect, useMemo, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./RunPayrollPage.css";

// const AdminRunPayroll = () => {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();

//   const [hourlyEmployees, setHourlyEmployees] = useState([]);
//   const [salariedEmployees, setSalariedEmployees] = useState([]);
//   const [payPeriod, setPayPeriod] = useState(null);

//   // NEW: editable fields for pay period
//   const [payPeriodForm, setPayPeriodForm] = useState({
//     start_date: "",
//     end_date: "",
//     pay_date: "",
//     frequency: "biweekly",
//   });

//   const [employeeInputs, setEmployeeInputs] = useState({});
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   const frequencyMultiplier = useMemo(
//     () => ({
//       weekly: 52,
//       biweekly: 26,
//       semimonthly: 24,
//       monthly: 12,
//     }),
//     []
//   );

//   // helpers for <input type="date" />
//   const toYYYYMMDD = (dateLike) => {
//     if (!dateLike) return "";
//     const d = new Date(dateLike);
//     if (Number.isNaN(d.getTime())) return "";
//     const yyyy = d.getFullYear();
//     const mm = String(d.getMonth() + 1).padStart(2, "0");
//     const dd = String(d.getDate()).padStart(2, "0");
//     return `${yyyy}-${mm}-${dd}`;
//   };

//   const getPayPeriodSalary = (annualSalary) => {
//     const freq = (payPeriodForm.frequency || payPeriod?.frequency || "").toLowerCase();
//     const multiplier = frequencyMultiplier[freq] || 1;
//     return (annualSalary / multiplier).toFixed(2);
//   };

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         const payPeriodResponse = await api.get(`/business/${businessId}/pay-period`);
//         const pp = payPeriodResponse.data;
//         setPayPeriod(pp);

//         // NEW: seed form with backend pay period values
//         setPayPeriodForm({
//           start_date: toYYYYMMDD(pp?.start_date),
//           end_date: toYYYYMMDD(pp?.end_date),
//           pay_date: toYYYYMMDD(pp?.pay_date),
//           frequency: (pp?.frequency || "biweekly").toLowerCase(),
//         });

//         const hourlyResponse = await api.get(`/businesses/${businessId}/employees/hourly`);
//         const hourly = hourlyResponse.data.map((employee) => ({
//           ...employee,
//           type: "hourly",
//         }));
//         setHourlyEmployees(hourly);

//         const salariedResponse = await api.get(`/businesses/${businessId}/employees/salary`);
//         const salaried = salariedResponse.data.map((employee) => ({
//           ...employee,
//           type: "salaried",
//         }));
//         setSalariedEmployees(salaried);

//         const initialInputs = {};
//         [...hourly, ...salaried].forEach((employee) => {
//           initialInputs[employee.id] = {
//             regularHours: null,
//             overtimeHours: null,
//             doubletimeHours: null,
//             cashAdvance: null,
//             bonus: null,
//             tips: null,
//             reimbursement: null,
//             other: null,
//             vacationHours: null,
//             vacationPay: null,
//           };
//         });
//         setEmployeeInputs(initialInputs);

//         setError(null);
//       } catch (err) {
//         console.error("Error fetching employees:", err);
//         setError("Failed to fetch employees. Please try again.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, [businessId]);

//   const handlePayPeriodChange = (field, value) => {
//     setPayPeriodForm((prev) => ({
//       ...prev,
//       [field]: value,
//     }));
//   };

//   const handleInputChange = (employeeId, field, value) => {
//     setEmployeeInputs((prev) => ({
//       ...prev,
//       [employeeId]: {
//         ...prev[employeeId],
//         [field]: value ? parseFloat(value) : null,
//       },
//     }));
//   };

//   const handleSubmit = async () => {
//     const collectedResults = [...hourlyEmployees, ...salariedEmployees].map((employee) => {
//       const perhourSalary = employee.salary_amt / 52 / 40 || null;
//       const employeeInput = employeeInputs[employee.id] || {};

//       const extraFields = {
//         cashAdvance: employeeInput.cashAdvance || 0,
//         bonus: employeeInput.bonus || 0,
//         tips: employeeInput.tips || 0,
//         reimbursement: employeeInput.reimbursement || 0,
//         other: employeeInput.other || 0,
//       };

//       // Use editable form values; fallback to payPeriod if needed
//       const startDate = payPeriodForm.start_date || toYYYYMMDD(payPeriod?.start_date);
//       const endDate = payPeriodForm.end_date || toYYYYMMDD(payPeriod?.end_date);
//       const payDate = payPeriodForm.pay_date || toYYYYMMDD(payPeriod?.pay_date);
//       const frequency = (payPeriodForm.frequency || payPeriod?.frequency || "").toLowerCase();

//       const commonFields = {
//         id: employee.id,
//         business_id: businessId,
//         business_state: payPeriod?.b_state,
//         start_date: startDate,
//         end_date: endDate,
//         pay_date: payDate,
//         frequency,
//         name: `${employee.first_name} ${employee.last_name}`,
//         st_add: employee.e_st_add,
//         e_suite: employee.e_suite,
//         e_zip: employee.e_zip,
//         filing_status: employee.filing_status,
//         e_state: employee.e_state,
//         e_city: employee.e_city,
//         e_county: employee.e_county,
//         dependents: employee.dependents || 0,
//         contrib_401k: employee.contrib_401k || 0,
//         donation: employee.donation || 0,
//         garnishment: employee.garnishment || 0,
//         additional_fed_tax: employee.additional_fed_tax || 0,
//         additional_state_tax: employee.additional_state_tax || 0,
//         work_loc: employee.work_loc,
//         perhour_salary: perhourSalary,
//         payment_type: employee.payment_type,
//         employer_mandated: employee.employer_mandated,
//         is_direct_dep: employee.is_direct_dep,
//         account_number: employee.account_number,
//         routing_number: employee.routing_number,
//       };

//       if (employee.type === "hourly") {
//         return {
//           ...commonFields,
//           ...extraFields,
//           hourly_rate: employee.hourly_amt,
//           regularHours:
//             employeeInput.regularHours !== null && employeeInput.regularHours !== undefined
//               ? employeeInput.regularHours
//               : 0,
//           overtimeHours: employeeInput.overtimeHours || 0,
//           doubletimeHours: employeeInput.doubletimeHours || 0,
//           vacationHours: employeeInput.vacationHours || 0,
//         };
//       }

//       return {
//         ...commonFields,
//         ...extraFields,
//         salary_amt: Number(getPayPeriodSalary(employee.salary_amt)),
//         vacationPay: employeeInput.vacationPay || 0,
//       };
//     });

//     try {
//       const response = await api.post(`/payroll/admin-entry`, { payrollData: collectedResults });
//       const { success, errors } = response.data;

//       if (errors?.length > 0) {
//         alert(
//           `Partial success:\n\nSuccess:\n${success
//             .map((item) => ` - ${item.name}`)
//             .join("\n")}\n\nErrors:\n${errors.join("\n")}`
//         );
//       } else {
//         alert("All payroll records processed successfully.");
//         navigate(`/dashboard/business/${businessId}/viewpayroll`);
//       }
//     } catch (err) {
//       console.error("Error submitting payroll data:", err);
//       alert("Failed to submit payroll data. Please try again.");
//     }
//   };

//   if (loading) return <p>Loading employees...</p>;
//   if (error) return <p>{error}</p>;

//   return (
//     <div className="run-body">
//       <h2>New Payroll</h2>


//       {/* NEW: editable pay period section (keeps same container class so your CSS still applies) */}
//       <div className="pay-period-payroll-cont">
//         <div className="ppl-pay">
//           <label>Start Date:</label>
//           <input
//             type="date"
//             className="pay-period-content"
//             value={payPeriodForm.start_date}
//             onChange={(e) => handlePayPeriodChange("start_date", e.target.value)}
//           />
//         </div>

//         <div className="ppl-pay">
//           <label>End Date:</label>
//           <input
//             type="date"
//             className="pay-period-content"
//             value={payPeriodForm.end_date}
//             onChange={(e) => handlePayPeriodChange("end_date", e.target.value)}
//           />
//         </div>

//         <div className="ppl-pay">
//           <label>Pay Date:</label>
//           <input
//             type="date"
//             className="pay-period-content"
//             value={payPeriodForm.pay_date}
//             onChange={(e) => handlePayPeriodChange("pay_date", e.target.value)}
//           />
//         </div>

//         <div className="ppl-pay">
//           <label>Frequency:</label>
//           <select
//             className="pay-period-content"
//             value={payPeriodForm.frequency}
//             onChange={(e) => handlePayPeriodChange("frequency", e.target.value)}
//           >
//             <option value="weekly">Weekly</option>
//             <option value="biweekly">Biweekly</option>
//             <option value="semimonthly">Semimonthly</option>
//             <option value="monthly">Monthly</option>
//           </select>
//         </div>
//       </div>

//       <table className="run-table">
//         <tbody className="run-tbody">
//           {[...hourlyEmployees, ...salariedEmployees].map((employee) => (
//             <tr className="run-row" key={employee.id}>
//               <td>
//                 {employee.first_name} {employee.last_name}
//               </td>
//               <td>{employee.type === "hourly" ? "Hourly" : "Salaried"}</td>
//               <td>
//                 {employee.type === "hourly"
//                   ? `$${employee.hourly_amt} per hour`
//                   : `$${getPayPeriodSalary(employee.salary_amt)} per period`}
//               </td>
//               <td>
//                 {employee.type === "hourly" && (
//                   <>
//                     <input
//                       type="number"
//                       placeholder="Regular Hours"
//                       value={employeeInputs[employee.id]?.regularHours ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(employee.id, "regularHours", e.target.value)
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="Overtime Hours"
//                       value={employeeInputs[employee.id]?.overtimeHours ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(employee.id, "overtimeHours", e.target.value)
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="Doubletime Hours"
//                       value={employeeInputs[employee.id]?.doubletimeHours ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(employee.id, "doubletimeHours", e.target.value)
//                       }
//                     />
//                     <input
//                       type="number"
//                       placeholder="Vacation Hours"
//                       value={employeeInputs[employee.id]?.vacationHours ?? ""}
//                       onChange={(e) =>
//                         handleInputChange(employee.id, "vacationHours", e.target.value)
//                       }
//                     />
//                   </>
//                 )}

//                 {employee.type === "salaried" && (
//                   <input
//                     type="number"
//                     placeholder="Vacation Pay"
//                     value={employeeInputs[employee.id]?.vacationPay ?? ""}
//                     onChange={(e) =>
//                       handleInputChange(employee.id, "vacationPay", e.target.value)
//                     }
//                   />
//                 )}

//                 <input
//                   type="number"
//                   placeholder="Cash Advance"
//                   value={employeeInputs[employee.id]?.cashAdvance ?? ""}
//                   onChange={(e) => handleInputChange(employee.id, "cashAdvance", e.target.value)}
//                 />
//                 <input
//                   type="number"
//                   placeholder="Bonus"
//                   value={employeeInputs[employee.id]?.bonus ?? ""}
//                   onChange={(e) => handleInputChange(employee.id, "bonus", e.target.value)}
//                 />
//                 <input
//                   type="number"
//                   placeholder="Tips"
//                   value={employeeInputs[employee.id]?.tips ?? ""}
//                   onChange={(e) => handleInputChange(employee.id, "tips", e.target.value)}
//                 />
//                 <input
//                   type="number"
//                   placeholder="Reimbursement"
//                   value={employeeInputs[employee.id]?.reimbursement ?? ""}
//                   onChange={(e) =>
//                     handleInputChange(employee.id, "reimbursement", e.target.value)
//                   }
//                 />
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table>

//       <div className="button-container">
//         <button onClick={handleSubmit}>Submit</button>
//       </div>
//     </div>
//   );
// };

// export default AdminRunPayroll;
